import React, { FunctionComponent, useState } from 'react';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import SelectInput from '@havenengineering/module-shared-library/dist/form/components/SelectInput';
import TextInput from '@havenengineering/module-shared-library/dist/form/components/TextInput';
import Button from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { SectionHeader } from '@havenengineering/module-shared-owners-ui/dist/components/SectionHeader/SectionHeader';
import clsx from 'clsx';
import { DateTime } from 'luxon';

import styles from './DatePicker.module.scss';

type Props = {
  title: string;
  onProceed: () => void;
  onFormChange: OnFormChange;
  name: string;
  value: null | string;
  buttonText: string;
  keyBoxCode?: string;
  availableSlots?: DrainDownSlot[];
};

const DatePicker: FunctionComponent<Props> = ({
  title,
  onProceed,
  onFormChange,
  name,
  value: slotId,
  buttonText,
  keyBoxCode,
  availableSlots = [],
}) => {
  const [selectedMonth, setSelectedMonth] = useState(
    availableSlots.length ? new Date(availableSlots[0].from) : new Date()
  );

  const hasAvailableSlot = () =>
    availableSlots.length && availableSlots.some((slot) => slot.available);

  const getMonths = () => {
    return availableSlots
      .map((slot) => DateTime.fromISO(slot.from).toFormat('LLLL yyyy'))
      .filter((month, index, months) => months.indexOf(month) === index)
      .map((month) => ({
        value: month,
        label: month,
      }));
  };

  const getWeeks = () => {
    return availableSlots
      .filter((slot) =>
        DateTime.fromISO(slot.from).hasSame(
          DateTime.fromJSDate(selectedMonth),
          'month'
        )
      )
      .map((slot, index) => {
        const isSelected = slot.id === getSelectedDate()?.id;
        const isDisabled = !slot.available;
        const date = DateTime.fromJSDate(new Date(slot.from));

        return (
          <Button
            variant="outlined"
            key={index}
            disabled={isDisabled}
            className={clsx(styles.week, {
              [styles.selected]: isSelected,
            })}
            onClick={() => handleDateSelect(slot.id)}
          >
            <span>
              Week Commencing {date.toFormat('d LLL')} (wk {date.weekNumber})
            </span>
            <span>
              {isDisabled && (
                <img
                  src="https://www.haven.com/assets/icons/inform-alert.svg"
                  alt="info"
                />
              )}
              {isDisabled
                ? 'Not available'
                : isSelected
                ? 'Selected'
                : 'Select'}
            </span>
          </Button>
        );
      });
  };

  const getSelectedDate = () => {
    return availableSlots.find((slot) => slot.id === slotId);
  };

  const handleDateSelect = (value: string) => {
    onFormChange({ target: { name: `${name}SlotId`, value } });
  };

  const handleMonthChange = (value: string | null) => {
    if (!value) return setSelectedMonth(new Date());
    setSelectedMonth(DateTime.fromFormat(value, 'LLLL yyyy').toJSDate());
  };

  return (
    <div className={styles.root}>
      <SectionHeader title={title} />
      {hasAvailableSlot() ? (
        <p className="owners-body">
          <strong>Pick a week that works best for you.</strong> <br />
          When would you like to book for?
        </p>
      ) : (
        <InlineMessaging
          type={InlineMessagingTheme.WARNING_THEME}
          title="Warning"
          message="There are no available slots. Please contact park for more
              information!"
          dismissible={false}
        />
      )}
      <div className={styles.content}>
        <SelectInput
          id="outlined-month-selector"
          value={DateTime.fromJSDate(selectedMonth).toFormat('LLLL yyyy')}
          onChange={(value) => handleMonthChange(value)}
          label="Month"
          options={getMonths()}
        />

        <div className={styles.weekPicker}>{getWeeks()}</div>
        {keyBoxCode !== undefined && (
          <TextInput
            id="keyBoxCode"
            onChange={(value) =>
              onFormChange({ target: { name: 'keyBoxCode', value } })
            }
            value={keyBoxCode}
            label="Key Box Code (if applicable)"
          />
        )}
        <Button
          className={styles.button}
          variant="contained"
          onClick={onProceed}
          disabled={!getSelectedDate()}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default DatePicker;
